"use client";

import React, { useContext, useEffect, useState } from "react";
import { DateRangePicker, Input } from "@panda0/panda0-ui";
import { addMonths, endOfMonth } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import { Controller, useFormContext } from "react-hook-form";

import useDepartments from "@/lib/swr/use-departments";
import { JobFormData } from "@/lib/types";
import CollaboratorComponent from "./basic/collaborator";
import JobDescriptionWizard from "./basic/job-description-wizard";
import YearsExperienceControl from "./experience-control";
import SalaryRangeControl from "./salary-range-control";
import { TagSelect } from "./tag-select";
import { JobModalContext } from "./types";

// Animation variants for elements
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 300, damping: 30 },
  },
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

const listItemVariants = {
  hidden: { opacity: 0, x: -10 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { type: "spring", stiffness: 400, damping: 30 },
  },
};

const popIn = {
  hidden: { scale: 0.95, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: { type: "spring", stiffness: 400, damping: 30 },
  },
};

function JobBasic() {
  const {
    register,
    control,
    watch,
    setValue,
    getValues,
    clearErrors,
    trigger,
    formState: { errors },
  } = useFormContext<JobFormData>();
  const { savedJobId, workspaceId, stateMachine, jobData } =
    useContext(JobModalContext);
  const domain = process.env.NEXT_PUBLIC_APP_SHORT_DOMAIN!;
  const { departments, loading: isDepartmentLoading } = useDepartments();
  const [showJobDescriptionPrompt, setShowJobDescriptionPrompt] =
    useState(false);
  const [hasJobDescription, setHasJobDescription] = useState<boolean | null>(
    null
  );
  const [formLoaded, setFormLoaded] = useState(false);

  const currentTime = new Date();
  const nextMonthEndDate = endOfMonth(addMonths(currentTime, 1));

  useEffect(() => {
    // Only run this if we have jobData and we've navigated away and come back
    if (jobData?.basic) {
      // Populate form with saved data
      Object.entries(jobData.basic).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          //@ts-ignore
          setValue(`basic.${key}`, value, { shouldDirty: false });
        }
      });
    }
  }, [jobData, setValue]);

  // Automatically load a department if available
  useEffect(() => {
    if (isDepartmentLoading || departments === null || !departments) return;

    if (!watch("basic.departmentId") && (departments?.length ?? 0) > 0) {
      setValue("basic.departmentId", departments[0].id, { shouldDirty: true });
    }
  }, [departments, isDepartmentLoading, setValue, watch]);

  // Generate a random link key for new jobs
  useEffect(() => {
    if (savedJobId || (savedJobId?.length ?? 0) > 4) {
      // Existing job - don't regenerate key
      return;
    }

    if (!watch("basic.key")) {
      (async function () {
        try {
          const res = await fetch(
            `/api/v1/links/random?domain=${domain}&workspaceId=${workspaceId}`
          );
          if (res.ok) {
            const key = await res.json();
            setValue("basic.key", key, { shouldDirty: true });
            setValue("basic.url", `https://${domain}/quiz/${key}`, {
              shouldDirty: true,
            });
          }
        } catch (error) {
          console.error("Error generating random key:", error);
        }
      })();
    }
  }, [workspaceId, savedJobId, setValue, domain, watch]);

  // Set form loaded after a delay to trigger animations
  useEffect(() => {
    const timer = setTimeout(() => {
      setFormLoaded(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Register required fields for validation
  register("basic.experienceStart", {
    required: "Experience start is required",
  });
  register("basic.experienceEnd", { required: "Experience end is required" });

  const experienceStart = watch("basic.experienceStart") || 0;
  const experienceEnd = watch("basic.experienceEnd") || 2;

  // Validate all basic fields with tracking
  const validateAllFields = async () => {
    stateMachine.trackEvent("job_basic_validate", {
      has_title: Boolean(watch("basic.title")),
      has_department: Boolean(watch("basic.departmentId")),
      experience_range: [
        watch("basic.experienceStart"),
        watch("basic.experienceEnd"),
      ],
    });

    return await trigger([
      "basic.title",
      "basic.departmentId",
      "basic.experienceStart",
      "basic.experienceEnd",
    ]);
  };

  // Handle extracted data with tracking
  const handleExtractedData = (extractedData: any) => {
    stateMachine.trackEvent("job_basic_ai_extraction", {
      has_title: Boolean(extractedData.title),
      has_experience: Boolean(extractedData.experienceStart !== undefined),
      has_positions: Boolean(extractedData.positions),
      has_tags: Boolean(extractedData.tags?.length),
      extraction_source: "job_description_wizard",
    });

    // Populate form with extracted data
    if (extractedData.title) {
      setValue("basic.title", extractedData.title, { shouldDirty: true });
    }

    if (
      extractedData.experienceStart !== undefined &&
      extractedData.experienceEnd !== undefined
    ) {
      setValue("basic.experienceStart", extractedData.experienceStart, {
        shouldDirty: true,
      });
      setValue("basic.experienceEnd", extractedData.experienceEnd, {
        shouldDirty: true,
      });
    }

    if (extractedData.positions) {
      setValue("basic.positions", extractedData.positions, {
        shouldDirty: true,
      });
    }

    // Also handle tags if available
    if (extractedData.tags && Array.isArray(extractedData.tags)) {
      setValue("basic.tags", extractedData.tags, { shouldDirty: true });
    }

    // Continue to the main form
    setShowJobDescriptionPrompt(false);
  };

  // Save form data
  const handleSave = async () => {
    stateMachine.trackEvent("job_basic_save_attempt", {
      form_data_size: JSON.stringify(getValues("basic")).length,
      is_new_job: !savedJobId,
    });

    if (await validateAllFields()) {
      return await stateMachine.saveStep("basic", true);
    }
    return false;
  };

  // Render job description prompt with animations
  if (showJobDescriptionPrompt) {
    return (
      <AnimatePresence>
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="bg-white rounded-xl shadow-lg max-w-3xl w-full mx-4 overflow-hidden p-6"
            initial={{ scale: 0.9, y: 10, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.9, y: 10, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            {hasJobDescription === null ? (
              <motion.div
                className="space-y-6"
                initial="hidden"
                animate="visible"
                variants={containerVariants}
              >
                <motion.h2
                  className="text-2xl font-bold text-neutral-900 mb-4"
                  variants={fadeInUp}
                >
                  Job Description Wizard
                </motion.h2>
                <motion.p className="text-neutral-700" variants={fadeInUp}>
                  Do you have a job description ready? We can use AI to help you
                  fill out the job details automatically.
                </motion.p>
                <motion.div
                  className="flex gap-4 justify-end"
                  variants={fadeInUp}
                >
                  <motion.button
                    onClick={() => setHasJobDescription(false)}
                    className="px-4 py-2 border border-neutral-300 rounded-md hover:bg-neutral-50 transition-colors"
                    whileHover={{
                      scale: 1.02,
                      backgroundColor: "rgb(249 250 251)",
                    }}
                    whileTap={{ scale: 0.98 }}
                  >
                    No, I'll fill it manually
                  </motion.button>
                  <motion.button
                    onClick={() => setHasJobDescription(true)}
                    className="px-4 py-2 bg-neutral-900 text-white rounded-md hover:bg-neutral-800 transition-colors"
                    whileHover={{
                      scale: 1.02,
                      backgroundColor: "rgb(31 41 55)",
                    }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Yes, I have a description
                  </motion.button>
                </motion.div>
              </motion.div>
            ) : hasJobDescription ? (
              <JobDescriptionWizard
                workspaceId={workspaceId!}
                onProcessed={handleExtractedData}
                onBack={() => setHasJobDescription(null)}
                onClose={() => setShowJobDescriptionPrompt(false)}
              />
            ) : (
              <motion.div
                className="space-y-6"
                initial="hidden"
                animate="visible"
                variants={containerVariants}
              >
                <motion.h2
                  className="text-2xl font-bold text-neutral-900 mb-4"
                  variants={fadeInUp}
                >
                  Job Description Wizard
                </motion.h2>
                <motion.p className="text-neutral-700" variants={fadeInUp}>
                  No problem! You can fill out the job details manually.
                </motion.p>
                <motion.div className="flex justify-end" variants={fadeInUp}>
                  <motion.button
                    onClick={() => setShowJobDescriptionPrompt(false)}
                    className="px-4 py-2 bg-neutral-900 text-white rounded-md hover:bg-neutral-800 transition-colors"
                    whileHover={{
                      scale: 1.02,
                      backgroundColor: "rgb(31 41 55)",
                    }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Continue to Job Form
                  </motion.button>
                </motion.div>
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Regular form rendering with animations
  return (
    <motion.div
      className="grid grid-cols-1 overflow-auto md:grid-cols-[3fr_1fr] md:divide-x"
      initial="hidden"
      animate={formLoaded ? "visible" : "hidden"}
      variants={containerVariants}
    >
      <motion.div
        className="scrollbar-hide flex flex-col px-6 md:overflow-auto"
        variants={fadeInUp}
      >
        <motion.div className="space-y-6" variants={containerVariants}>
          {savedJobId?.length && savedJobId.length > 0 ? null : (
            <motion.div variants={fadeInUp}>
              <JobDescriptionWizard
                workspaceId={workspaceId!}
                onProcessed={handleExtractedData}
                onBack={() => setHasJobDescription(null)}
                onClose={() => setShowJobDescriptionPrompt(false)}
              />
            </motion.div>
          )}

          <motion.div variants={fadeInUp}>
            <label
              htmlFor="basic.title"
              className="block text-sm font-medium text-neutral-700"
            >
              Title *
            </label>
            <Controller
              name="basic.title"
              control={control}
              rules={{ required: "Job title is required" }}
              render={({ field }) => (
                <motion.div className="w-full" whileTap={{ scale: 0.995 }}>
                  <Input
                    placeholder="E.g. Senior Software Engineer"
                    value={field.value || ""}
                    onChange={(e) => field.onChange(e.target.value)}
                    type="text"
                    className="w-full !max-w-none mt-1 block rounded-md border-neutral-300
                    text-neutral-900 placeholder-neutral-400 focus:border-neutral-500
                    focus:outline-none focus:ring-neutral-500 sm:text-sm"
                  />
                  <AnimatePresence>
                    {errors.basic?.title && (
                      <motion.p
                        className="mt-1 text-sm text-red-600"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        * {errors.basic.title.message}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </motion.div>
              )}
            />
          </motion.div>

          {/* Department & Openings in 3 columns */}
          <motion.div
            className="grid grid-cols-3 gap-4"
            variants={containerVariants}
          >
            <motion.div variants={listItemVariants}>
              <label
                htmlFor="basic.priority"
                className="block text-sm font-medium text-neutral-700"
              >
                Priority
              </label>
              <Controller
                name="basic.priority"
                control={control}
                rules={{ required: "Priority is required" }}
                render={({ field, fieldState: { error } }) => (
                  <motion.div className="w-full" whileTap={{ scale: 0.995 }}>
                    <select
                      {...field}
                      id="priority"
                      className={`mt-1 block w-full rounded-md border-neutral-300 ${error ? "border-red-500" : "border-neutral-300"
                        } text-neutral-900 placeholder-neutral-400 focus:border-neutral-500 focus:outline-none focus:ring-neutral-500 sm:text-sm`}
                    >
                      <option value="" disabled>
                        Select a priority
                      </option>
                      <option value="HIGH">High</option>
                      <option value="MEDIUM">Medium</option>
                      <option value="LOW">Low</option>
                    </select>
                    <AnimatePresence>
                      {errors.basic?.priority && (
                        <motion.p
                          className="mt-1 text-sm text-red-600"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                        >
                          * {errors.basic?.priority.message}
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </motion.div>
                )}
              />
            </motion.div>

            <motion.div variants={listItemVariants}>
              <label
                htmlFor="basic.departmentId"
                className="block text-sm font-medium text-neutral-700"
              >
                Category *
              </label>
              <Controller
                name="basic.departmentId"
                control={control}
                rules={{ required: "Category is required" }}
                render={({ field }) => (
                  <motion.div className="w-full" whileTap={{ scale: 0.995 }}>
                    <select
                      {...field}
                      id="departmentId"
                      disabled={isDepartmentLoading}
                      className={`mt-1 block w-full rounded-md border-neutral-300 ${errors.basic?.departmentId ? "border-red-300" : ""
                        } text-neutral-900 placeholder-neutral-400 focus:border-neutral-500 focus:outline-none focus:ring-neutral-500 sm:text-sm`}
                    >
                      <option value="" disabled>
                        Select a department
                      </option>
                      {departments &&
                        departments.map((dept) => (
                          <option key={dept.id} value={dept.id}>
                            {dept.name}
                          </option>
                        ))}
                    </select>
                    <AnimatePresence>
                      {errors.basic?.departmentId && (
                        <motion.p
                          className="mt-1 text-sm text-red-600"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                        >
                          * {errors.basic?.departmentId.message}
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </motion.div>
                )}
              />
            </motion.div>

            <motion.div variants={listItemVariants}>
              <label
                htmlFor="basic.positions"
                className="block text-sm font-medium text-neutral-700"
              >
                Positions
              </label>
              <Controller
                name="basic.positions"
                control={control}
                render={({ field }) => (
                  <motion.div className="w-full" whileTap={{ scale: 0.995 }}>
                    <Input
                      placeholder="1"
                      type="number"
                      value={field.value || ""}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                      className="mt-1 block w-full rounded-md border-neutral-300
                      text-neutral-900 placeholder-neutral-400 focus:border-neutral-500
                      focus:outline-none focus:ring-neutral-500 sm:text-sm"
                    />
                  </motion.div>
                )}
              />
            </motion.div>
          </motion.div>

          {/* Experience */}
          <motion.div className="mb-4" variants={fadeInUp}>
            <YearsExperienceControl
              defaultMin={experienceStart}
              defaultMax={experienceEnd}
              onChange={(min, max) => {
                clearErrors("basic.experienceStart");
                clearErrors("basic.experienceEnd");
                setValue("basic.experienceStart", min, { shouldDirty: true });
                setValue("basic.experienceEnd", max, { shouldDirty: true });
              }}
            />
            <AnimatePresence>
              {(errors.basic?.experienceStart ||
                errors.basic?.experienceEnd) && (
                  <motion.p
                    className="mt-1 text-sm text-red-600"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    * Experience range is required
                  </motion.p>
                )}
            </AnimatePresence>
          </motion.div>

          <motion.div
            className="pt-4 border-t border-neutral-200"
            variants={fadeInUp}
          >
            <CollaboratorComponent
              workspaceId={workspaceId!}
              jobId={savedJobId}
            />
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Right sidebar - Job information */}
      <motion.div
        className="scrollbar-hide flex flex-col space-y-6 md:overflow-auto"
        variants={fadeInUp}
        transition={{ delay: 0.2 }}
      >
        <motion.div
          className="rounded-xl border border-neutral-200 bg-neutral-50 p-4"
          variants={popIn}
        >
          <motion.h3
            className="mb-4 text-lg font-medium"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Tags & Integration
          </motion.h3>

          <motion.div className="mb-4" variants={fadeInUp}>
            <label
              htmlFor="basic.campaignEnd"
              className="block text-sm font-medium text-neutral-700"
            >
              Application Deadline
            </label>
            <Controller
              name="basic.campaignStart"
              control={control}
              render={({ field: startField }) => (
                <Controller
                  name="basic.campaignEnd"
                  control={control}
                  render={({ field: endField }) => (
                    <motion.div whileTap={{ scale: 0.995 }}>
                      <DateRangePicker
                        defaultValue={{
                          from: startField.value || currentTime,
                          to: endField.value || nextMonthEndDate,
                        }}
                        onChange={(d) => {
                          startField.onChange(d?.from || currentTime);
                          endField.onChange(d?.to || nextMonthEndDate);
                        }}
                        className="mt-1 w-full"
                      />
                    </motion.div>
                  )}
                />
              )}
            />
          </motion.div>

          {/* Tags */}
          <motion.div variants={fadeInUp} transition={{ delay: 0.4 }}>
            <TagSelect />
          </motion.div>

          {/* External Job Information */}
          <motion.div
            className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 mt-4 mb-4"
            variants={popIn}
            transition={{ delay: 0.5 }}
          >
            <motion.h3
              className="mb-4 text-lg font-medium"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              External Job Information
            </motion.h3>

            {/* External Job ID */}
            <motion.div className="mb-4" variants={fadeInUp}>
              <label
                htmlFor="basic.externalId"
                className="block text-sm font-medium text-neutral-700"
              >
                External Job ID
              </label>

              <Controller
                name="basic.externalId"
                control={control}
                render={({ field }) => (
                  <motion.div className="w-full" whileTap={{ scale: 0.995 }}>
                    <Input
                      placeholder="External reference ID"
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      type="text"
                      className="mt-1 block w-full rounded-md border-neutral-300
                      text-neutral-900 placeholder-neutral-400 focus:border-neutral-500
                      focus:outline-none focus:ring-neutral-500 sm:text-sm"
                    />
                  </motion.div>
                )}
              />
            </motion.div>

            {/* Apply URL */}
            <motion.div variants={fadeInUp}>
              <label
                htmlFor="basic.JOBSETTINGS_APPLICATION_URL"
                className="block text-sm font-medium text-neutral-700"
              >
                Application URL
              </label>

              <Controller
                name="basic.JOBSETTINGS_APPLICATION_URL"
                control={control}
                render={({ field }) => (
                  <motion.div className="w-full" whileTap={{ scale: 0.995 }}>
                    <Input
                      placeholder="https://example.com/apply"
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      type="url"
                      className="mt-1 block w-full rounded-md border-neutral-300
                      text-neutral-900 placeholder-neutral-400 focus:border-neutral-500
                      focus:outline-none focus:ring-neutral-500 sm:text-sm"
                    />
                  </motion.div>
                )}
              />
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Decorative spline (light curved path in the background) */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <svg
          width="100%"
          height="100%"
          className="absolute inset-0 opacity-5"
          preserveAspectRatio="none"
        >
          <motion.path
            d="M0,100 C150,200 350,0 500,100 C650,200 750,0 1000,100 L1000,1000 L0,1000 Z"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 0.5 }}
            transition={{ duration: 2, ease: "easeInOut" }}
          />
          <motion.path
            d="M0,300 C250,400 300,200 500,300 C700,400 750,200 1000,300 L1000,1000 L0,1000 Z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 0.3 }}
            transition={{ duration: 2.5, ease: "easeInOut", delay: 0.3 }}
          />
        </svg>
      </div>
    </motion.div>
  );
}

export default React.memo(JobBasic);
